import { useEffect } from "react"
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon.jsx"
import Loadable from "../../components/Loadable"
import PageTitle from "../../components/PageTitle.jsx"
import ProfileNav from "./components/ProfileNav"
import ProfileTools from "./components/ProfileTools.jsx"

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'
import { fetchProfile, updateProfile } from '../../state/actions/profileActions'
import { fetchProfileSchema } from '../../state/actions/profileSchemaActions'
import { NotificationTypes, addNotification } from "../../state/actions/notificationActions"

import { profileSignal, profileLoadedSignal, profileErrorSignal, profileCcSchemaSignal } from '../../state/signals/profileSignals'
import { profileSchemaVersionSignal } from '../../state/signals/profileSchemaSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

export default function ProfileDeviceList() {

    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { beId, profileId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/profiles`)
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewProfiles) {
            addNotification('You are not authorized to view UTG profiles for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditProfiles
    })
    useEffect(() => {
        if (beIdChanged) return
        fetchProfile(beId, profileId, config, accessToken).then(
            () => {
                fetchSchemaManifest().then(
                    () => {
                        fetchProfileSchema(beId, profileCcSchemaSignal.value, schemaManifestSignal.value, config, accessToken)
                    }
                )
            }
        )
    }, [beId, beIdChanged, profileId, config, accessToken])

    const devicesComputed = useComputed(() => {
        // devices is a subset of profile
        if (profileSignal.value) {
            return profileSignal.value?.config?.deviceConfig?.device ?? []
        }
        return []
    })

    const showDeleteModalSignal = useSignal(false)
    const confirmDeviceTypeSignal = useSignal('')
    const confirmTaskDescSignal = useSignal('')
    const accordionData = [
        { name: 'Devices', data: devicesComputed.value },
        // { name: 'Device Schemas', data: deviceSchemas },
        { name: 'Component Profile', data: profileSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: schemaManifestSignal.value }
    ]

    function DeviceTableRows({ devices }) {
        let rows = []
        devices.forEach(device => {
            rows.push(
                <DeviceRow device={device} key={`${device.deviceType}${device.taskDesc}`} />
            )
        })

        return (
            <tbody>{rows}</tbody>
        )
    }

    function DeviceRow({ device }) {
        let deviceType = device.deviceType
        let taskDesc = device.taskDesc
        let encodedDeviceType = encodeURIComponent(deviceType)
        return (
            <tr>
                <td><Link to={`./${encodedDeviceType}?desc=${taskDesc}`}>{device.deviceType}</Link></td>
                <td>{device.taskDesc}</td>
                <td>{device.threadType}</td>
                <td>
                    {canEditComputed.value && <Link to={`./${encodedDeviceType}?desc=${taskDesc}`}><Button variant="outline-primary"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button></Link>}
                    {canEditComputed.value && <Button className="mx-2" variant="outline-danger" onClick={() => showModal(deviceType, taskDesc)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>}
                </td>
            </tr>
        )
    }

    const deleteDevice = () => {
        showDeleteModalSignal.value = false

        // filter out device to be deleted
        const filteredDevices = profileSignal.value.config.deviceConfig.device.filter(device =>
            !(device.deviceType == confirmDeviceTypeSignal.value && device.taskDesc == confirmTaskDescSignal.value));

        // set newly filtered device array in componentConfigData    
        profileSignal.value.config.deviceConfig.device = filteredDevices

        //build data object for updateComponentConfig request
        let data = {
            id: profileSignal.value.id,
            name: profileSignal.value.name,
            businessEntityId: profileSignal.value.businessEntityId,
            config: profileSignal.value.config,
        }

        // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
        data.config.utgGlobal.ccschema = profileSchemaVersionSignal.value

        //console.log(data)
        updateProfile(beId, profileId, data, config, accessToken, true)
            .then(() => {
                addNotification('The Device was successfully removed.', NotificationTypes.SUCCESS, true)
            })
            .catch(error => {
                console.log(error.toString())
                addNotification(error.toString(), NotificationTypes.ERROR, false)
                return false
            })
            .finally(() => {
                // must return false to avoid postback
                return false
            })
    }

    const showModal = (deviceType, taskDesc) => {
        // just set the state and the modal will appear
        confirmDeviceTypeSignal.value = deviceType
        confirmTaskDescSignal.value = taskDesc
        showDeleteModalSignal.value = true
    }

    function NewDeviceDropdown() {
        if (schemaManifestSignal.value && schemaManifestSignal.value['device-profiles'].length) {
            let deviceOptions = schemaManifestSignal.value['device-profiles'].map(s => {
                let escapedName = encodeURIComponent(s.name)
                return (
                    <Dropdown.Item href={`/be/${beId}/profiles/${profileId}/devices/${escapedName}`} key={s.name}>{s.name}</Dropdown.Item>
                )
            })

            return (
                <DropdownButton id="dropdown-add-device"
                    variant="outline-secondary"
                    title="New Device"
                    className="mx-3">
                    {deviceOptions}
                </DropdownButton>
            )
        }
    }

    return (
        <>
            <ConfirmationModal
                show={showDeleteModalSignal.value}
                handleCancel={
                    () => showDeleteModalSignal.value = false
                }
                handleConfirm={
                    () => deleteDevice()
                }
                message={`Are you sure you want to delete ${confirmTaskDescSignal.value}? This action cannot be undone.`}
                title={`Deleting ${confirmTaskDescSignal.value}`}
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                    Profile List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    profileLoadedSignal.value ? <>Manage {profileSignal.value.name}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title='Profile Devices' canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[profileLoadedSignal]} errorSignals={[profileErrorSignal]}>
                    <ProfileTools profile={profileSignal.value}></ProfileTools>
                </Loadable>
            </Stack>
            <ProfileNav activeKey="3"></ProfileNav>

            <Table responsive striped hover size="sm">
                <thead>
                    <tr>
                        <th>Device Type</th>
                        <th>Device Description</th>
                        <th>Thread Type</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <DeviceTableRows devices={devicesComputed.value} />
            </Table>
            {canEditComputed.value && <NewDeviceDropdown />}
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}